import React, { useMemo, useState } from 'react';
import {
  Crown,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  UserRound,
  UserRoundX,
} from 'lucide-react';

import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import InvitedEmailsPanel from './InvitedEmailsPanel';
import { useAdvisoryPartnerAccountsState } from '../hooks/useAdvisoryPartnerAccountsState';

const ACCESS_SOURCE_META = {
  primary_tier: {
    label: 'Primary tier',
    className: 'border-lime-500/20 bg-lime-500/10 text-lime-300',
  },
  additional_role: {
    label: 'Additional role',
    className: 'border-cyan-500/20 bg-cyan-500/10 text-cyan-300',
  },
};

const STATUS_META = {
  active: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300',
  former: 'border-slate-500/20 bg-slate-500/10 text-slate-300',
  trial: 'border-amber-500/20 bg-amber-500/10 text-amber-300',
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const normalize = (value) => String(value || '').trim().toLowerCase();

export default function AdvisoryPartnerAccountsTab({ adminFetch }) {
  const {
    overview,
    loading,
    refreshing,
    savingIds,
    fetchAccounts,
    promoteToPrimaryTier,
    removePartnerAccess,
  } = useAdvisoryPartnerAccountsState({ adminFetch });
  const [searchTerm, setSearchTerm] = useState('');

  const partners = useMemo(() => {
    const query = normalize(searchTerm);
    if (!query) return overview.partners || [];

    return (overview.partners || []).filter((partner) => (
      normalize(partner.name).includes(query)
      || normalize(partner.email).includes(query)
      || normalize(partner.tier).includes(query)
    ));
  }, [overview.partners, searchTerm]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-aqua" />
      </div>
    );
  }

  const stats = overview.stats || {};

  return (
    <div className="space-y-6">
      <Card className="border border-lime-200 bg-gradient-to-br from-[#f7fcee] via-white to-[#edf5e8] p-6 dark:border-lime-500/15 dark:bg-gradient-to-br dark:from-[#171d12] dark:via-[#141914] dark:to-[#10150f]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-lime-700 dark:text-lime-300">
          Advisory Partners lane
        </p>
        <h2 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
          Keep the pattern-seeing partners visible and intentionally entrusted
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-gray-300">
          This surface is for the people who can see the Advisory layer of the platform. Use it to confirm who belongs
          here, whether access is layered or primary, and whether the advisory circle still reflects the mission&apos;s
          real needs.
        </p>
      </Card>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-[240px] flex-1">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              placeholder="Search advisory partner accounts..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="border-[#2a2a32] bg-[#16161a] pl-9 text-white"
            />
          </div>
        </div>
        <Button
          onClick={() => fetchAccounts({ silent: true })}
          variant="outline"
          className="border-[#2a2a32] text-white hover:bg-[#16161a]"
        >
          {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          ['Total accounts', stats.total_accounts || 0, UserRound],
          ['Primary tier', stats.primary_tier_accounts || 0, Crown],
          ['Additional role', stats.additional_role_accounts || 0, ShieldCheck],
          ['Inactive', stats.inactive_accounts || 0, UserRoundX],
        ].map(([label, value, Icon]) => (
          <Card key={label} className="border-[#2a2a32] bg-[#1e1e24] p-5">
            <div className="mb-3 flex items-start justify-between">
              <Icon className="h-6 w-6 text-aqua" />
            </div>
            <p className="text-sm text-gray-400">{label}</p>
            <span className="text-2xl font-bold text-white">{value}</span>
          </Card>
        ))}
      </div>

      <Card className="border-[#2a2a32] bg-[#1e1e24] p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-white">Advisory Partner accounts</h3>
            <p className="mt-1 text-sm text-gray-400">
              These are users with `advisory_partner` as either their primary tier or an additional role.
            </p>
          </div>
          <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-300">
            {partners.length} shown
          </Badge>
        </div>

        {!partners.length ? (
          <p className="py-8 text-center text-gray-500">No advisory partner accounts matched this view.</p>
        ) : (
          <div className="space-y-4">
            {partners.map((partner) => {
              const accessMeta = ACCESS_SOURCE_META[partner.access_source] || ACCESS_SOURCE_META.additional_role;
              const awaitingSignIn = partner.awaiting_sign_in || !partner.user_id;
              const isSaving = Boolean(savingIds[partner.user_id]);
              const statusClass = STATUS_META[normalize(partner.status)] || STATUS_META.active;
              const canPromote = partner.access_source !== 'primary_tier';

              return (
                <div key={partner.user_id || partner.email} className="rounded-xl bg-[#16161a] p-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium text-white">{partner.name || 'Advisory Partner'}</p>
                          <Badge className={accessMeta.className}>{accessMeta.label}</Badge>
                          {awaitingSignIn ? (
                            <Badge className="border-amber-500/20 bg-amber-500/10 text-amber-300">Awaiting sign-in</Badge>
                          ) : null}
                          <Badge className={statusClass}>{partner.status || 'active'}</Badge>
                        </div>
                        <p className="mt-1 text-sm text-gray-400">{partner.email || 'No email on file'}</p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-300">
                          Primary: {partner.tier || 'member'}
                        </Badge>
                        {(partner.additional_roles || []).map((role) => (
                          <Badge key={`${partner.user_id}-${role}`} className="border-cyan-500/20 bg-cyan-500/10 text-cyan-300">
                            {role}
                          </Badge>
                        ))}
                      </div>

                      <div className="grid gap-2 text-sm text-gray-400 md:grid-cols-2 xl:grid-cols-3">
                        <p>{`Created: ${formatDate(partner.created_at)}`}</p>
                        <p>{`Updated: ${formatDate(partner.updated_at)}`}</p>
                        <p>{`Last active: ${formatDate(partner.last_active_at)}`}</p>
                      </div>
                    </div>

                    <div className="flex flex-col items-start gap-3 lg:items-end">
                      {awaitingSignIn ? (
                        <p className="text-sm text-gray-400">Access activates when they sign in with this email.</p>
                      ) : (
                        <>
                          {canPromote ? (
                            <Button
                              onClick={() => promoteToPrimaryTier(partner)}
                              disabled={isSaving}
                              variant="outline"
                              className="border-[#2a2a32] text-white hover:bg-[#11161d]"
                            >
                              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Crown className="mr-2 h-4 w-4" />}
                              Make primary tier
                            </Button>
                          ) : null}
                          <Button
                            onClick={() => removePartnerAccess(partner)}
                            disabled={isSaving}
                            variant="outline"
                            className="border-rose-500/25 text-rose-200 hover:bg-rose-500/10"
                          >
                            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserRoundX className="mr-2 h-4 w-4" />}
                            Remove access
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <InvitedEmailsPanel adminFetch={adminFetch} />
    </div>
  );
}
